import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Title} from '@angular/platform-browser';
import {DataService} from '../../Services/data.service';
import {BsToastService} from '../../Services/bs-toast-service';
import {AppConfigService} from '../../Services/app-config.service';
import {ConfirmDialogService} from '../../Services/confirm-dialog.service';

@Component({
  selector: 'app-stripe-dashboard',
  templateUrl: './stripe-dashboard.component.html',
  styleUrls: ['./stripe-dashboard.component.css'],
  standalone: false
})
export class StripeDashboardComponent implements OnInit {

  apiBaseUrl = '';
  buttonsDisabled = false;

  // Summary KPIs
  summary: any = null;
  summaryLoading = true;
  summaryError = false;

  // Webhook Health
  webhookHealth: any = null;
  webhookLoading = true;
  webhookError = false;

  // Failed Payouts (AP)
  failedPayouts: any[] = [];
  failedPayoutsTotal = 0;
  failedPayoutsLoading = true;
  failedPayoutsError = false;

  // Failed AR Payments
  failedAr: any = null;
  failedArLoading = true;
  failedArError = false;

  // Success Payouts (AP)
  successPayouts: any[] = [];
  successPayoutsTotal = 0;
  successPayoutsLoading = true;
  successPayoutsError = false;

  // Success AR Payments
  successAr: any = null;
  successArLoading = true;
  successArError = false;

  // Live Balance
  balance: any = null;
  balanceLoading = true;
  balanceError = false;

  constructor(
    private titleService: Title,
    private tableDataService: DataService,
    private notification: BsToastService,
    private appConfig: AppConfigService,
    private http: HttpClient,
    private confirm: ConfirmDialogService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle('Stripe Dashboard');
    this.apiBaseUrl = this.appConfig.apiBaseUrl;
    this.loadSummary();
    this.loadWebhookHealth();
    this.loadFailedPayouts();
    this.loadFailedAr();
    this.loadSuccessPayouts();
    this.loadSuccessAr();
    this.loadBalance();
  }

  // Loaders

  loadSummary() {
    this.summaryLoading = true;
    this.summaryError = false;
    this.tableDataService.getData('Stripe/dashboard/summary').subscribe({
      next: (result: any) => {
        this.summary = result;
        this.summaryLoading = false;
      },
      error: () => {
        this.summaryError = true;
        this.summaryLoading = false;
      }
    });
  }

  loadWebhookHealth() {
    this.webhookLoading = true;
    this.webhookError = false;
    this.tableDataService.getData('Stripe/dashboard/webhook-health').subscribe({
      next: (result: any) => {
        this.webhookHealth = result;
        this.webhookLoading = false;
      },
      error: () => {
        this.webhookError = true;
        this.webhookLoading = false;
      }
    });
  }

  loadFailedPayouts() {
    this.failedPayoutsLoading = true;
    this.failedPayoutsError = false;
    this.tableDataService.getData('Stripe/dashboard/failed-payouts').subscribe({
      next: (result: any) => {
        this.failedPayouts = result?.data ?? [];
        this.failedPayoutsTotal = result?.total ?? 0;
        this.failedPayoutsLoading = false;
      },
      error: () => {
        this.failedPayoutsError = true;
        this.failedPayoutsLoading = false;
      }
    });
  }

  loadFailedAr() {
    this.failedArLoading = true;
    this.failedArError = false;
    this.tableDataService.getData('Stripe/dashboard/failed-ar-payments').subscribe({
      next: (result: any) => {
        this.failedAr = result;
        this.failedArLoading = false;
      },
      error: () => {
        this.failedArError = true;
        this.failedArLoading = false;
      }
    });
  }

  loadSuccessPayouts() {
    this.successPayoutsLoading = true;
    this.successPayoutsError = false;
    this.tableDataService.getData('Stripe/dashboard/success-payouts').subscribe({
      next: (result: any) => {
        this.successPayouts = result?.data ?? [];
        this.successPayoutsTotal = result?.total ?? 0;
        this.successPayoutsLoading = false;
      },
      error: () => {
        this.successPayoutsError = true;
        this.successPayoutsLoading = false;
      }
    });
  }

  loadSuccessAr() {
    this.successArLoading = true;
    this.successArError = false;
    this.tableDataService.getData('Stripe/dashboard/success-ar-payments').subscribe({
      next: (result: any) => {
        this.successAr = result;
        this.successArLoading = false;
      },
      error: () => {
        this.successArError = true;
        this.successArLoading = false;
      }
    });
  }

  loadBalance() {
    this.balanceLoading = true;
    this.balanceError = false;
    this.tableDataService.getData('Stripe/dashboard/balance').subscribe({
      next: (result: any) => {
        this.balance = result;
        this.balanceLoading = false;
      },
      error: () => {
        this.balanceError = true;
        this.balanceLoading = false;
      }
    });
  }

  // Helpers

  get webhookBadgeClass(): string {
    if (!this.webhookHealth) {
      return 'badge bg-secondary';
    }
    return this.webhookHealth.is_stale ? 'badge bg-danger' : 'badge bg-success';
  }

  get webhookBadgeLabel(): string {
    if (!this.webhookHealth) {
      return 'Unknown';
    }
    return this.webhookHealth.is_stale ? 'Stale' : 'Healthy';
  }

  get totalFailedArCount(): number {
    if (!this.failedAr) {
      return 0;
    }
    return (this.failedAr.insufficient_funds?.count ?? 0)
      + (this.failedAr.no_account?.count ?? 0)
      + (this.failedAr.account_closed?.count ?? 0)
      + (this.failedAr.other?.count ?? 0);
  }

  get totalSuccessArCount(): number {
    if (!this.successAr) {
      return 0;
    }
    return this.successAr.total ?? this.successArGroups().reduce((s, g) => s + g.count, 0);
  }

  formatCurrency(amount: number, currency = 'usd'): string {
    if (amount == null) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: currency.toUpperCase()}).format(amount);
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) {
      return '—';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return '—';
    }
    return d.toLocaleString('en-US', {month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'});
  }

  failedArGroups(): { key: string; label: string; count: number; items: any[] }[] {
    if (!this.failedAr) {
      return [];
    }
    return [
      {
        key: 'insufficient_funds',
        label: 'Insufficient Funds',
        count: this.failedAr.insufficient_funds?.count ?? 0,
        items: this.failedAr.insufficient_funds?.items ?? []
      },
      {key: 'no_account', label: 'No Account', count: this.failedAr.no_account?.count ?? 0, items: this.failedAr.no_account?.items ?? []},
      {
        key: 'account_closed',
        label: 'Account Closed',
        count: this.failedAr.account_closed?.count ?? 0,
        items: this.failedAr.account_closed?.items ?? []
      },
      {key: 'other', label: 'Other', count: this.failedAr.other?.count ?? 0, items: this.failedAr.other?.items ?? []},
    ].filter(g => g.count > 0);
  }

  successArGroups(): { key: string; label: string; count: number; items: any[] }[] {
    if (!this.successAr) {
      return [];
    }
    return [
      {
        key: 'insufficient_funds',
        label: 'Insufficient Funds',
        count: this.successAr.insufficient_funds?.count ?? 0,
        items: this.successAr.insufficient_funds?.items ?? []
      },
      {key: 'no_account', label: 'No Account', count: this.successAr.no_account?.count ?? 0, items: this.successAr.no_account?.items ?? []},
      {
        key: 'account_closed',
        label: 'Account Closed',
        count: this.successAr.account_closed?.count ?? 0,
        items: this.successAr.account_closed?.items ?? []
      },
      {key: 'other', label: 'Other', count: this.successAr.other?.count ?? 0, items: this.successAr.other?.items ?? []},
    ].filter(g => g.count > 0);
  }

  refreshAll() {
    this.loadSummary();
    this.loadWebhookHealth();
    this.loadFailedPayouts();
    this.loadFailedAr();
    this.loadSuccessPayouts();
    this.loadSuccessAr();
    this.loadBalance();
  }
}
